import { useCallback, useEffect, useRef, useState } from "react";
import { Camera, Check, Clock3, LocateFixed, MapPin, RefreshCw, ShieldCheck, UserRound, WifiOff, X } from "lucide-react";
import { ApiError, checkLocation, getServerTime, getToday, submitAttendance, type SessionUser } from "../lib/api";

type ProofState = "idle" | "loading" | "ready" | "error";

const thaiDateTime = (iso: string) => new Intl.DateTimeFormat("th-TH", {
  dateStyle: "medium", timeStyle: "medium", timeZone: "Asia/Bangkok"
}).format(new Date(iso));

export function Attendance({ user }: { user: SessionUser }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [serverTime, setServerTime] = useState(new Date().toISOString());
  const [camera, setCamera] = useState<ProofState>("idle");
  const [location, setLocation] = useState<ProofState>("loading");
  const [locationCheck, setLocationCheck] = useState<{ inside: boolean; distance: number; radius: number } | null>(null);
  const [position, setPosition] = useState<GeolocationCoordinates | null>(null);
  const [photo, setPhoto] = useState<Blob | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [today, setToday] = useState<Awaited<ReturnType<typeof getToday>> | null>(null);

  useEffect(() => {
    let active = true;
    getServerTime().then((time) => active && setServerTime(time));
    getToday().then((result) => active && setToday(result)).catch((error) => active && setNotice(error instanceof ApiError ? error.message : "โหลดข้อมูลเวลาทำงานไม่ได้"));
    const timer = window.setInterval(() => setServerTime((current) => new Date(new Date(current).getTime() + 1000).toISOString()), 1000);
    return () => { active = false; window.clearInterval(timer); };
  }, []);

  useEffect(() => () => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    if (preview) URL.revokeObjectURL(preview);
  }, [preview]);

  const startCamera = useCallback(async () => {
    setCamera("loading");
    setNotice(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user", width: { ideal: 720 }, height: { ideal: 960 } }, audio: false });
      streamRef.current?.getTracks().forEach((track) => track.stop());
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setCamera("ready");
    } catch {
      setCamera("error");
      setNotice("เปิดกล้องไม่ได้ โปรดอนุญาตการใช้กล้องแล้วลองอีกครั้ง");
    }
  }, []);

  const capturePhoto = useCallback(async () => {
    const video = videoRef.current;
    if (!video || video.videoWidth === 0) return;
    const freshServerTime = await getServerTime();
    setServerTime(freshServerTime);
    const canvas = document.createElement("canvas");
    canvas.width = Math.min(768, video.videoWidth);
    canvas.height = Math.round(canvas.width * (video.videoHeight / video.videoWidth));
    const context = canvas.getContext("2d");
    if (!context) return;
    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    const plateHeight = Math.max(56, Math.round(canvas.height * 0.085));
    context.fillStyle = "rgba(12, 30, 18, 0.86)";
    context.fillRect(0, canvas.height - plateHeight, canvas.width, plateHeight);
    context.fillStyle = "white";
    context.font = `600 ${Math.max(18, Math.round(canvas.width * 0.028))}px system-ui`;
    context.textBaseline = "middle";
    context.fillText(thaiDateTime(freshServerTime), 20, canvas.height - plateHeight / 2);
    context.textAlign = "right";
    context.fillText(position ? `GPS ±${Math.round(position.accuracy)} ม.` : "เวลาจากเซิร์ฟเวอร์", canvas.width - 20, canvas.height - plateHeight / 2);
    context.textAlign = "left";
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.82));
    if (!blob) return;
    if (preview) URL.revokeObjectURL(preview);
    setPhoto(blob);
    setPreview(URL.createObjectURL(blob));
    streamRef.current?.getTracks().forEach((track) => track.stop());
    setCamera("ready");
  }, [position, preview]);

  const requestLocation = useCallback(() => {
    setLocation("loading");
    setLocationCheck(null);
    setNotice(null);
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        setPosition(coords);
        checkLocation(coords.latitude, coords.longitude).then((result) => {
          setLocationCheck(result);
          setLocation(result.inside ? "ready" : "error");
          if (!result.inside) setNotice(`อยู่นอกพื้นที่ร้าน ${result.distance} เมตร กรุณาเข้าใกล้ร้านแล้วตรวจอีกครั้ง`);
        }).catch((error) => {
          setLocation("error");
          setNotice(error instanceof ApiError ? error.message : "ตรวจสอบพื้นที่ร้านไม่ได้ กรุณาลองอีกครั้ง");
        });
      },
      () => { setPosition(null); setLocation("error"); setNotice("หาตำแหน่งไม่ได้ โปรดเปิด GPS และอนุญาตตำแหน่งให้เว็บไซต์"); },
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 15_000 }
    );
  }, []);

  useEffect(() => requestLocation(), [requestLocation]);

  const confirm = useCallback(async () => {
    if (!photo || !position || location !== "ready" || !today || today.nextAction === "complete") return;
    setSubmitting(true);
    setNotice(null);
    try {
      const result = await submitAttendance({ photo, latitude: position.latitude, longitude: position.longitude, action: today.nextAction });
      setServerTime(result.recordedAt);
      setSuccess(true);
    } catch (error) {
      setNotice(error instanceof ApiError ? error.message : "เชื่อมต่อระบบไม่ได้ กรุณาตรวจอินเทอร์เน็ตแล้วลองอีกครั้ง");
    } finally {
      setSubmitting(false);
    }
  }, [location, photo, position, today]);

  if (success) {
    return (
      <main className="employee-page employee-success" id="main-content">
        <div className="success-seal"><Check size={48} strokeWidth={2.5} /></div>
        <p className="success-title">{today?.nextAction === "check-out" ? "ออกงานแล้ว" : "เข้างานแล้ว"}</p>
        <time>{new Intl.DateTimeFormat("th-TH", { hour: "2-digit", minute: "2-digit", second: "2-digit", timeZone: "Asia/Bangkok" }).format(new Date(serverTime))} น.</time>
        <p>หลักฐานครบทั้งรูปถ่ายและ GPS ในพื้นที่ร้าน</p>
        <div className="success-proof" aria-label="หลักฐานที่บันทึกแล้ว">
          <span><Camera size={17} /> รูปพร้อมเวลา</span><span><MapPin size={17} /> GPS ในพื้นที่</span>
        </div>
        {notice && <p className="inline-notice"><WifiOff size={17} /> {notice}</p>}
        <button className="button secondary" onClick={() => window.location.reload()}>กลับหน้าวันนี้</button>
      </main>
    );
  }

  const readyCount = Number(Boolean(photo)) + Number(location === "ready");
  const locationDetail = locationCheck?.inside
    ? `อยู่ในพื้นที่ · ห่างจุดลงเวลา ${locationCheck.distance} ม.`
    : locationCheck
      ? `อยู่นอกพื้นที่ ${locationCheck.distance} ม. · รัศมี ${locationCheck.radius} ม.`
      : location === "error" ? "เปิด GPS แล้วแตะเพื่อตรวจใหม่" : "กำลังตรวจอัตโนมัติ";
  return (
    <main className="employee-page" id="main-content">
      <header className="employee-header">
        <div>
          <p className="welcome">สวัสดี {user.fullName.split(" ")[0]}</p>
          <h1>{today ? `เริ่มงานมาตรฐาน ${today.workHours.start_time} น.` : "กำลังโหลดเวลาทำงาน…"}</h1>
        </div>
        <span className="avatar-fallback"><UserRound size={21} /></span>
      </header>

      <section className="work-hours-line" aria-label="เวลาทำงานมาตรฐาน">
        <Clock3 size={18} /><span>{user.position} · {today ? `${today.workHours.start_time}–${today.workHours.end_time}` : "กำลังโหลด…"}</span><strong>วันนี้</strong>
      </section>

      <section className="camera-stage" aria-labelledby="camera-title">
        <div className="camera-copy">
          <div><h2 id="camera-title">ถ่ายรูปยืนยัน</h2><p>จัดใบหน้าให้อยู่ในกรอบ ระบบจะประทับเวลาให้อัตโนมัติ</p></div>
          <span className="server-time"><span></span>{new Intl.DateTimeFormat("th-TH", { hour: "2-digit", minute: "2-digit", second: "2-digit", timeZone: "Asia/Bangkok" }).format(new Date(serverTime))}</span>
        </div>
        <div className={`camera-viewport ${camera === "error" ? "has-error" : ""}`}>
          {preview ? <img src={preview} alt="รูปยืนยันตัวตนที่เพิ่งถ่าย" /> : <video ref={videoRef} playsInline muted aria-label="ภาพจากกล้องหน้า" />}
          {!preview && camera === "idle" && <div className="camera-placeholder"><Camera size={38} /><p>กล้องจะเปิดเมื่อคุณพร้อม</p></div>}
          {!preview && camera === "ready" && <div className="face-guide" aria-hidden="true"><span></span></div>}
          {preview && <div className="captured-badge"><Check size={16} /> ถ่ายแล้ว</div>}
        </div>
        {!preview && camera !== "ready" && <button className="button camera-button" onClick={startCamera} disabled={camera === "loading"}><Camera size={19} /> {camera === "loading" ? "กำลังเปิดกล้อง…" : camera === "error" ? "ลองเปิดกล้องอีกครั้ง" : "เปิดกล้อง"}</button>}
        {!preview && camera === "ready" && <button className="button camera-button" onClick={capturePhoto}><Camera size={19} /> ถ่ายรูป</button>}
        {preview && <button className="text-button" onClick={() => { setPhoto(null); setPreview(null); void startCamera(); }}><RefreshCw size={17} /> ถ่ายใหม่</button>}
      </section>

      <section className="proof-section" aria-labelledby="proof-title">
        <div className="proof-heading"><div><h2 id="proof-title">หลักฐานการลงเวลา</h2><p>{readyCount} จาก 2 รายการพร้อมแล้ว</p></div><div className="proof-progress" aria-label={`พร้อม ${readyCount} จาก 2`}><span style={{ width: `${readyCount / 2 * 100}%` }} /></div></div>
        <div className="proof-rail">
          <ProofButton icon={Camera} label="รูปถ่าย" detail={photo ? "ประทับเวลาแล้ว" : "ยังไม่ได้ถ่าย"} state={photo ? "ready" : camera} onClick={photo ? undefined : startCamera} />
          <ProofButton icon={LocateFixed} label="GPS อัตโนมัติ" detail={locationDetail} state={location} onClick={location === "error" ? requestLocation : undefined} />
        </div>
      </section>

      {notice && <p className="inline-notice" role="status"><ShieldCheck size={17} /> {notice}</p>}

      <div className="sticky-confirm">
        <button className="button primary large" disabled={readyCount < 2 || submitting || !today || today.nextAction === "complete"} onClick={confirm}>
          {submitting ? "กำลังบันทึกเวลา…" : today?.nextAction === "complete" ? "ลงเวลาครบแล้ววันนี้" : <><Check size={22} /> {today?.nextAction === "check-out" ? "ยืนยันออกงาน" : "ยืนยันเข้างาน"}</>}
        </button>
        <p><ShieldCheck size={14} /> รูปและพิกัดใช้เพื่อตรวจสอบการลงเวลาเท่านั้น</p>
      </div>
    </main>
  );
}

function ProofButton({ icon: Icon, label, detail, state, onClick }: { icon: typeof Camera; label: string; detail: string; state: ProofState; onClick?: () => void }) {
  return (
    <button className={`proof-item is-${state}`} onClick={onClick} disabled={!onClick || state === "loading"}>
      <span className="proof-icon"><Icon size={22} /></span>
      <span><strong>{label}</strong><small>{state === "loading" ? "กำลังตรวจ…" : detail}</small></span>
      <span className="proof-state">{state === "ready" ? <Check size={16} /> : state === "error" ? <X size={16} /> : ""}</span>
    </button>
  );
}
